import React, { useEffect, useMemo, useRef, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { adminApi } from "../../lib/adminApi";
import { toast } from "sonner";
import { Music, Plus, Edit2, Trash2, ImagePlus, Search, Star, StarOff, Tag } from "lucide-react";

const TIERS = ["premium", "platinum"];
const TIER_COLOR = {
  premium: { bg: "rgba(0,105,92,0.30)", border: "rgba(0,150,136,0.55)", text: "#4DB6AC", glow: "0 0 12px rgba(0,150,136,0.35)" },
  platinum: { bg: "rgba(255,213,79,0.15)", border: "rgba(255,213,79,0.55)", text: "#FFD54F", glow: "0 0 12px rgba(255,213,79,0.35)" },
};

const EMPTY_CAT = { name: "", thumbnail_url: "", icon: "", description: "", benefits: "", is_popular: false };
const EMPTY_SONG = {
  title: "", artist: "WOU Frequency Music\u2122", frequency: "125 Hz", duration: "15:00",
  category: "", url: "", thumbnail_url: "", subscription_tier: "premium", preview_seconds: 30, benefits: [], notes: "",
};

function TierBadge({ tier }) {
  const key = (tier || "premium").toLowerCase();
  const c = TIER_COLOR[key] || TIER_COLOR.premium;
  return (
    <span
      className="inline-flex items-center px-2.5 py-1 rounded text-[11px] font-bold uppercase tracking-wider"
      style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.text, boxShadow: c.glow }}
      data-testid={`tier-badge-${key}`}
    >
      {key.toUpperCase()}
    </span>
  );
}

function ThumbUploader({ value, onChange, testid = "thumb-uploader" }) {
  const inputRef = useRef(null);
  const onFile = (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Max image size is 10 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(file);
  };
  const onDrop = (e) => {
    e.preventDefault();
    onFile(e.dataTransfer.files?.[0]);
  };
  return (
    <div className="space-y-3">
      <p className="text-[11px] uppercase tracking-wider text-muted-soft">Upload thumbnail image (JPG, PNG, WebP, GIF — Max 10MB)</p>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className="rounded-xl border-2 border-dashed flex flex-col items-center justify-center py-7 cursor-pointer transition-colors hover:border-[#d49bff]"
        style={{ borderColor: "rgba(212,155,255,0.45)", background: "rgba(123,31,162,0.06)" }}
        data-testid={`${testid}-dropzone`}
      >
        {value ? (
          <img src={value} alt="thumb" className="w-24 h-24 rounded-lg object-cover mb-2" />
        ) : (
          <ImagePlus size={28} className="text-[#d49bff] mb-2" />
        )}
        <p className="text-sm">
          <span className="text-[#90CAF9] font-semibold">Click to upload</span>{" "}
          <span className="text-muted-soft">or drag and drop</span>
        </p>
        <p className="text-[10px] text-muted-soft mt-1">Max size: 10MB • Formats: image/*</p>
        <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => onFile(e.target.files?.[0])} data-testid={`${testid}-input`} />
      </div>
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-white/10" />
        <span className="text-[10px] uppercase tracking-wider text-muted-soft">OR</span>
        <div className="h-px flex-1 bg-white/10" />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-muted-soft mb-1">Thumbnail URL (paste link)</p>
        <input
          className="glass-input"
          value={value && value.startsWith("data:") ? "" : (value || "")}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://example.com/image.jpg or /static/categor…"
          data-testid={`${testid}-url`}
        />
        <p className="text-[10px] text-muted-soft mt-1">Paste a direct URL to thumbnail image or local path</p>
      </div>
    </div>
  );
}

function CategoryModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => initial ? {
    ...EMPTY_CAT,
    ...initial,
    benefits: Array.isArray(initial.benefits) ? initial.benefits.join(", ") : (initial.benefits || ""),
  } : EMPTY_CAT);
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        thumbnail_url: form.thumbnail_url || null,
        icon: form.icon || null,
        description: form.description || null,
        benefits: form.benefits.split(",").map((s) => s.trim()).filter(Boolean),
        is_popular: !!form.is_popular,
      };
      if (initial?.id) await adminApi.put(`/music/categories/${initial.id}`, payload);
      else await adminApi.post("/music/categories", payload);
      toast.success(initial?.id ? "Category updated" : "Category added");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={save}
        className="glass-card-strong w-full max-w-xl max-h-[92vh] overflow-y-auto p-6"
        style={{ background: "rgba(8,10,18,0.92)", borderColor: "rgba(212,155,255,0.35)" }}
        data-testid="category-modal"
      >
        <h2 className="text-2xl font-bold mb-5" style={{ color: "#E1BEE7" }}>
          {initial?.id ? "Edit Category" : "Add New Category"}
        </h2>

        <div className="space-y-5">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-soft">Name</label>
            <input className="glass-input mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="cat-name" />
          </div>

          <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <p className="text-sm font-semibold mb-3" style={{ color: "#FFE082" }}>Thumbnail Image Management</p>
            <ThumbUploader value={form.thumbnail_url} onChange={(v) => setForm({ ...form, thumbnail_url: v })} testid="cat-thumb" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-soft">Icon (Emoji) – Optional</label>
            <input
              className="glass-input mt-1"
              value={form.icon}
              onChange={(e) => setForm({ ...form, icon: e.target.value })}
              placeholder="🎵 (Enter an emoji – optional)"
              maxLength={4}
              data-testid="cat-icon"
            />
            <p className="text-[10px] text-muted-soft mt-1">Optional: Enter an emoji to represent this category (e.g., 🎵, 🌸, ✨)</p>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-soft">Description</label>
            <textarea
              className="glass-input mt-1 min-h-[88px]"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="cat-description"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-soft">Benefits (comma-separated)</label>
            <input
              className="glass-input mt-1"
              value={form.benefits}
              onChange={(e) => setForm({ ...form, benefits: e.target.value })}
              placeholder="e.g., Stress relief, Relaxation, Healing"
              data-testid="cat-benefits"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer" data-testid="cat-popular-toggle">
            <input type="checkbox" checked={form.is_popular} onChange={(e) => setForm({ ...form, is_popular: e.target.checked })} />
            <span className="text-sm">Mark as <b style={{ color: "#FFE082" }}>Most Popular</b></span>
          </label>
        </div>

        <div className="flex gap-3 mt-7 justify-end">
          <button type="button" onClick={onClose} className="btn-outline px-5" data-testid="cat-cancel">Cancel</button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2.5 rounded-xl font-bold disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#9C27B0,#d49bff)", color: "#fff", boxShadow: "0 4px 18px rgba(156,39,176,0.45)" }}
            data-testid="cat-save"
          >
            {initial?.id ? "Save Changes" : "Add Category"}
          </button>
        </div>
      </form>
    </div>
  );
}

function SongModal({ initial, categories, onClose, onSaved }) {
  const [form, setForm] = useState(() => initial ? { ...EMPTY_SONG, ...initial } : EMPTY_SONG);
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.url.trim()) { toast.error("Title and URL are required"); return; }
    setSaving(true);
    try {
      const benefitsArr = Array.isArray(form.benefits)
        ? form.benefits
        : String(form.benefits || "").split(",").map((s) => s.trim()).filter(Boolean);
      const payload = {
        title: form.title.trim(),
        artist: form.artist || null,
        frequency: form.frequency,
        duration: form.duration,
        category: form.category || (categories[0]?.name ?? ""),
        url: form.url.trim(),
        thumbnail_url: form.thumbnail_url || null,
        subscription_tier: form.subscription_tier,
        benefits: benefitsArr,
        notes: form.notes || null,
      };
      if (initial?.id) await adminApi.put(`/music/${initial.id}`, payload);
      else await adminApi.post("/music", payload);
      toast.success(initial?.id ? "Song updated" : "Song added");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={save}
        className="glass-card-strong w-full max-w-xl max-h-[92vh] overflow-y-auto p-6"
        style={{ background: "rgba(8,10,18,0.92)", borderColor: "rgba(212,155,255,0.35)" }}
        data-testid="song-modal"
      >
        <h2 className="text-2xl font-bold mb-5" style={{ color: "#E1BEE7" }}>
          {initial?.id ? "Edit Song" : "Add New Song"}
        </h2>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs uppercase tracking-wider text-muted-soft">Title</label>
              <input className="glass-input mt-1" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="song-title" />
            </div>
            <div className="col-span-2">
              <label className="text-xs uppercase tracking-wider text-muted-soft">Artist</label>
              <input className="glass-input mt-1" value={form.artist} onChange={(e) => setForm({ ...form, artist: e.target.value })} data-testid="song-artist" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-soft">Frequency</label>
              <input className="glass-input mt-1" required value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} placeholder="125 Hz" data-testid="song-frequency" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-soft">Duration (mm:ss)</label>
              <input className="glass-input mt-1" required value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="15:00" data-testid="song-duration" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-soft">Category</label>
              <select
                className="glass-input mt-1"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="song-category"
              >
                <option value="" style={{ background: "#0A0A0A" }}>— Select category —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.name} style={{ background: "#0A0A0A" }}>{c.icon ? `${c.icon} ` : ""}{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-soft">Subscription tier</label>
              <select
                className="glass-input mt-1"
                value={form.subscription_tier}
                onChange={(e) => setForm({ ...form, subscription_tier: e.target.value })}
                data-testid="song-tier"
              >
                {TIERS.map((t) => (
                  <option key={t} value={t} style={{ background: "#0A0A0A" }}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-soft">Audio URL (MP3)</label>
            <input className="glass-input mt-1" required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" data-testid="song-url" />
          </div>

          <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <p className="text-sm font-semibold mb-3" style={{ color: "#FFE082" }}>Thumbnail (optional)</p>
            <ThumbUploader value={form.thumbnail_url} onChange={(v) => setForm({ ...form, thumbnail_url: v })} testid="song-thumb" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-soft">Benefits (comma-separated)</label>
            <input
              className="glass-input mt-1"
              value={Array.isArray(form.benefits) ? form.benefits.join(", ") : (form.benefits || "")}
              onChange={(e) => setForm({ ...form, benefits: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              placeholder="e.g., Root Stimulation, Deep Calm, Photosynthesis"
              data-testid="song-benefits"
            />
            {Array.isArray(form.benefits) && form.benefits.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {form.benefits.map((b) => (
                  <span
                    key={b}
                    className="px-2.5 py-1 rounded-full text-[11px] font-medium"
                    style={{ background: "rgba(156,39,176,0.5)", color: "#fff", border: "1px solid rgba(212,155,255,0.4)" }}
                  >
                    {b}
                  </span>
                ))}
              </div>
            )}
            <p className="text-[10px] text-muted-soft mt-1.5">Surfaced to users as chips on the Music screen and Now-Playing panel.</p>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-soft">Notes</label>
            <input className="glass-input mt-1" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="song-notes" />
          </div>
        </div>

        <div className="flex gap-3 mt-7 justify-end">
          <button type="button" onClick={onClose} className="btn-outline px-5" data-testid="song-cancel">Cancel</button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2.5 rounded-xl font-bold disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#9C27B0,#d49bff)", color: "#fff", boxShadow: "0 4px 18px rgba(156,39,176,0.45)" }}
            data-testid="song-save"
          >
            {initial?.id ? "Save Changes" : "Add Song"}
          </button>
        </div>
      </form>
    </div>
  );
}

function CategoriesTab({ categories, reload, onEdit, onAdd }) {
  const togglePopular = async (cat) => {
    try {
      await adminApi.patch(`/music/categories/${cat.id}/popular`, { is_popular: !cat.is_popular });
      toast.success(cat.is_popular ? "Removed from popular" : "Marked popular");
      reload();
    } catch { toast.error("Failed"); }
  };
  const del = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}"? Songs will keep this label but will no longer match a category.`)) return;
    try { await adminApi.delete(`/music/categories/${cat.id}`); toast.success("Deleted"); reload(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-2xl font-extrabold" style={{ color: "#E1BEE7", textShadow: "0 0 22px rgba(156,39,176,0.45)" }}>
          Categories Management
        </h2>
        <button
          onClick={onAdd}
          className="px-5 py-2.5 rounded-xl font-bold inline-flex items-center gap-2"
          style={{ background: "linear-gradient(135deg,#9C27B0,#d49bff)", color: "#fff", boxShadow: "0 4px 18px rgba(156,39,176,0.45)" }}
          data-testid="add-category-btn"
        >
          <Plus size={16} /> Add New Category
        </button>
      </div>

      {categories.length === 0 ? (
        <div className="glass-card p-10 text-center">
          <Tag size={26} className="mx-auto text-[#d49bff] mb-3" />
          <p className="text-soft text-sm">No categories yet. Add one to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="categories-grid">
          {categories.map((c) => (
            <div
              key={c.id}
              className="relative rounded-2xl overflow-hidden p-5 flex flex-col"
              style={{
                background: c.is_popular ? "linear-gradient(160deg, rgba(70,30,5,0.92), rgba(20,12,30,0.92))" : "rgba(10,12,22,0.88)",
                border: c.is_popular ? "1px solid rgba(255,193,7,0.55)" : "1px solid rgba(255,255,255,0.08)",
                boxShadow: c.is_popular ? "0 8px 28px rgba(255,143,0,0.15)" : "0 8px 28px rgba(0,0,0,0.45)",
              }}
              data-testid={`category-card-${c.id}`}
            >
              {c.is_popular && (
                <span
                  className="absolute top-0 left-0 px-3 py-1 text-[10px] font-extrabold tracking-wider"
                  style={{ background: "#FFC107", color: "#1a0f00", borderBottomRightRadius: 10 }}
                  data-testid={`popular-badge-${c.id}`}
                >
                  ⭐ MOST POPULAR
                </span>
              )}

              <div className="flex items-start gap-3 mt-5">
                <div
                  className="w-16 h-16 rounded-xl flex-shrink-0 flex items-center justify-center overflow-hidden"
                  style={{ background: "rgba(123,31,162,0.18)", border: "1px solid rgba(212,155,255,0.3)" }}
                >
                  {c.thumbnail_url ? (
                    <img src={c.thumbnail_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span style={{ fontSize: 32 }}>{c.icon || "🎵"}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-extrabold uppercase tracking-wide truncate">{c.name}</h3>
                  <p className="text-xs text-muted-soft mt-0.5">{c.song_count || 0} song{(c.song_count || 0) === 1 ? "" : "s"}</p>
                </div>
                <div className="flex flex-col items-end gap-1.5 text-xs">
                  <button onClick={() => onEdit(c)} className="text-[#d49bff] hover:underline font-semibold" data-testid={`cat-edit-${c.id}`}>Edit</button>
                  <button
                    onClick={() => togglePopular(c)}
                    className="font-semibold inline-flex items-center gap-1"
                    style={{ color: c.is_popular ? "#FF8F00" : "#FFD700" }}
                    data-testid={`cat-popular-${c.id}`}
                  >
                    {c.is_popular ? <><StarOff size={11} /> Remove Popular</> : <><Star size={11} /> Set Popular</>}
                  </button>
                  <button onClick={() => del(c)} className="text-red-400 hover:underline font-semibold" data-testid={`cat-delete-${c.id}`}>Delete</button>
                </div>
              </div>

              {c.description && (
                <p className="text-sm text-soft mt-4 leading-relaxed line-clamp-6">{c.description}</p>
              )}

              {Array.isArray(c.benefits) && c.benefits.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-semibold mb-2" style={{ color: "#E1BEE7" }}>Benefits:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {c.benefits.map((b) => (
                      <span
                        key={b}
                        className="px-2.5 py-1 rounded-full text-[11px] font-medium"
                        style={{ background: "rgba(156,39,176,0.7)", color: "#fff", border: "1px solid rgba(212,155,255,0.4)" }}
                      >
                        {b}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SongsTab({ tracks, categories, stats, limits, reload, onEdit, onAdd }) {
  const [query, setQuery] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [editingLimits, setEditingLimits] = useState(false);
  const [limitDraft, setLimitDraft] = useState({ premium: 0, platinum: 0 });
  const [savingLimits, setSavingLimits] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tracks.filter((t) => {
      if (filterCat && (t.category || "") !== filterCat) return false;
      if (!q) return true;
      return (
        (t.title || "").toLowerCase().includes(q) ||
        (t.artist || "").toLowerCase().includes(q)
      );
    });
  }, [tracks, query, filterCat]);

  const del = async (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    try { await adminApi.delete(`/music/${t.id}`); toast.success("Deleted"); reload(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-2xl font-extrabold" style={{ color: "#E1BEE7", textShadow: "0 0 22px rgba(156,39,176,0.45)" }}>
          Songs Management
        </h2>
        <button
          onClick={onAdd}
          className="px-5 py-2.5 rounded-xl font-bold inline-flex items-center gap-2"
          style={{ background: "linear-gradient(135deg,#9C27B0,#d49bff)", color: "#fff", boxShadow: "0 4px 18px rgba(156,39,176,0.45)" }}
          data-testid="add-song-btn"
        >
          <Plus size={16} /> Add New Song
        </button>
      </div>

      {/* Distribution stats */}
      <div
        className="rounded-2xl p-6 mb-6"
        style={{ background: "linear-gradient(135deg, rgba(74,20,140,0.45), rgba(123,31,162,0.30))", border: "1px solid rgba(212,155,255,0.22)" }}
        data-testid="song-distribution"
      >
        <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
          <h3 className="text-xl font-bold">Song Distribution by Plan</h3>
          {!editingLimits ? (
            <button
              onClick={() => { setLimitDraft({ premium: Number(limits?.premium ?? 0), platinum: Number(limits?.platinum ?? 0) }); setEditingLimits(true); }}
              className="px-3 py-1.5 rounded-lg text-xs font-bold inline-flex items-center gap-1.5"
              style={{ background: "rgba(212,155,255,0.18)", border: "1px solid rgba(212,155,255,0.45)", color: "#E1BEE7" }}
              data-testid="limits-edit-btn"
            >
              <Edit2 size={12} /> Edit Counts
            </button>
          ) : (
            <div className="inline-flex items-center gap-2">
              <button
                onClick={async () => {
                  try {
                    setSavingLimits(true);
                    await adminApi.put("/music/plan-limits", {
                      premium: Math.max(0, Math.floor(Number(limitDraft.premium) || 0)),
                      platinum: Math.max(0, Math.floor(Number(limitDraft.platinum) || 0)),
                    });
                    toast.success("Plan counts updated");
                    setEditingLimits(false);
                    reload();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Failed to update");
                  } finally { setSavingLimits(false); }
                }}
                disabled={savingLimits}
                className="px-3 py-1.5 rounded-lg text-xs font-bold"
                style={{ background: "linear-gradient(135deg,#9C27B0,#d49bff)", color: "#fff" }}
                data-testid="limits-save-btn"
              >
                {savingLimits ? "Saving…" : "Save"}
              </button>
              <button
                onClick={() => setEditingLimits(false)}
                className="px-3 py-1.5 rounded-lg text-xs font-bold"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.75)" }}
                data-testid="limits-cancel-btn"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {TIERS.map((tier) => {
            const limitVal = Number(limits?.[tier] ?? 0);
            const actualCount = stats[tier] || 0;
            const c = TIER_COLOR[tier];
            const copy = {
              premium: "30-second preview for free users · Unlimited full access for Premium & Platinum",
              platinum: "30-second preview for Free & Premium · Unlimited full access for Platinum only",
            };
            return (
              <div
                key={tier}
                className="rounded-xl p-5"
                style={{ background: "rgba(10,12,22,0.6)", border: `1px solid ${c.border}`, boxShadow: c.glow }}
                data-testid={`stat-${tier}`}
              >
                <p className="text-xs uppercase tracking-wider font-bold" style={{ color: c.text }}>{tier} Plan</p>
                {editingLimits ? (
                  <input
                    type="number"
                    min={0}
                    value={limitDraft[tier]}
                    onChange={(e) => setLimitDraft({ ...limitDraft, [tier]: e.target.value })}
                    className="mt-2 w-28 bg-transparent text-4xl font-extrabold outline-none rounded-lg px-2 py-1"
                    style={{ color: c.text, border: `1px dashed ${c.border}` }}
                    data-testid={`limit-input-${tier}`}
                  />
                ) : (
                  <p className="text-4xl font-extrabold mt-2" style={{ color: c.text }} data-testid={`limit-value-${tier}`}>{limitVal}</p>
                )}
                <p className="text-[11px] mt-2 font-semibold" style={{ color: c.text }}>Songs</p>
                <p className="text-[10px] text-soft mt-1">Currently in library: <b>{actualCount}</b></p>
                <p className="text-[11px] text-soft mt-3 leading-relaxed">{copy[tier]}</p>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-soft mt-5 leading-relaxed">
          <b style={{ color: "#E1BEE7" }}>How access works:</b> Every song is either Premium or Platinum.
          {" "}Free users get a 30-second preview of every song.
          {" "}Premium subscribers play Premium songs in full and get a 30-second preview of Platinum songs.
          {" "}Platinum subscribers play everything in full and can create personal playlists.
        </p>
      </div>

      {/* Search + filter */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-soft">Search Songs</label>
          <div className="relative mt-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-soft" />
            <input
              className="glass-input pl-9"
              placeholder="Search by title or artist…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="song-search"
            />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-soft">Filter by Category</label>
          <select
            className="glass-input mt-1"
            value={filterCat}
            onChange={(e) => setFilterCat(e.target.value)}
            data-testid="song-filter-cat"
          >
            <option value="" style={{ background: "#0A0A0A" }}>All Categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.name} style={{ background: "#0A0A0A" }}>{c.icon ? `${c.icon} ` : ""}{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Songs table */}
      <div className="glass-card-strong overflow-hidden" data-testid="songs-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-muted-soft border-b border-white/10">
                <th className="px-4 py-3">Song</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Frequency</th>
                <th className="px-4 py-3">Duration</th>
                <th className="px-4 py-3">Subscription</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-soft">No songs match your filter.</td></tr>
              ) : (
                filtered.map((t) => (
                  <tr key={t.id} className="border-b border-white/5 hover:bg-white/3" data-testid={`song-row-${t.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-11 h-11 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0"
                          style={{ background: "rgba(123,31,162,0.2)" }}
                        >
                          {t.thumbnail_url ? (
                            <img src={t.thumbnail_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Music size={16} className="text-[#d49bff]" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold truncate">{t.title}</p>
                          <p className="text-[11px] text-muted-soft truncate">{t.artist || "—"}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-soft">{t.category || "—"}</td>
                    <td className="px-4 py-3" style={{ color: "#FFE082" }}>{t.frequency}</td>
                    <td className="px-4 py-3 text-soft">{t.duration}</td>
                    <td className="px-4 py-3"><TierBadge tier={t.subscription_tier || "premium"} /></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => onEdit(t)} className="text-[#90CAF9] hover:underline font-semibold mr-3" data-testid={`song-edit-${t.id}`}>Edit</button>
                      <button onClick={() => del(t)} className="text-red-400 hover:underline font-semibold" data-testid={`song-del-${t.id}`}>Delete</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function MusicLibraryScreen() {
  const [tab, setTab] = useState("categories");
  const [categories, setCategories] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [stats, setStats] = useState({});
  const [limits, setLimits] = useState({});
  const [editingCat, setEditingCat] = useState(null); // null | { } | { ...cat }
  const [editingSong, setEditingSong] = useState(null);

  const loadCategories = async () => {
    try { const { data } = await adminApi.get("/music/categories"); setCategories(data.categories || []); }
    catch { toast.error("Could not load categories"); }
  };
  const loadSongs = async () => {
    try {
      const { data } = await adminApi.get("/music");
      setTracks(data.tracks || []);
      setStats(data.stats || {});
      setLimits(data.limits || {});
    } catch { toast.error("Could not load songs"); }
  };
  useEffect(() => { loadCategories(); loadSongs(); }, []);

  return (
    <AdminLayout bgImage="/Zen%20Playback.webp" bgOverlay="rgba(0,0,0,0.62)">
      <div className="mb-6">
        <h1 className="text-3xl font-extrabold" style={{ color: "#E1BEE7", textShadow: "0 0 24px rgba(156,39,176,0.55)" }}>
          Music Library
        </h1>
        <p className="text-muted-soft text-sm mt-1">Manage plant-frequency music categories and songs.</p>
      </div>

      <div className="inline-flex p-1 rounded-xl mb-6" style={{ background: "rgba(10,12,22,0.78)", border: "1px solid rgba(212,155,255,0.25)" }}>
        {[
          { id: "categories", label: "Categories", count: categories.length },
          { id: "songs", label: "Songs", count: tracks.length },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className="px-5 py-2 rounded-lg text-sm font-semibold transition-all"
            style={
              tab === t.id
                ? { background: "linear-gradient(135deg,#9C27B0,#d49bff)", color: "#fff", boxShadow: "0 2px 14px rgba(156,39,176,0.45)" }
                : { color: "rgba(255,255,255,0.6)" }
            }
            data-testid={`tab-${t.id}`}
          >
            {t.label} <span className="opacity-70 ml-1">({t.count})</span>
          </button>
        ))}
      </div>

      {tab === "categories" ? (
        <CategoriesTab
          categories={categories}
          reload={loadCategories}
          onEdit={(c) => setEditingCat(c)}
          onAdd={() => setEditingCat({})}
        />
      ) : (
        <SongsTab
          tracks={tracks}
          categories={categories}
          stats={stats}
          limits={limits}
          reload={loadSongs}
          onEdit={(t) => setEditingSong(t)}
          onAdd={() => setEditingSong({})}
        />
      )}

      {editingCat && (
        <CategoryModal
          initial={editingCat.id ? editingCat : null}
          onClose={() => setEditingCat(null)}
          onSaved={() => { setEditingCat(null); loadCategories(); }}
        />
      )}
      {editingSong && (
        <SongModal
          initial={editingSong.id ? editingSong : null}
          categories={categories}
          onClose={() => setEditingSong(null)}
          onSaved={() => { setEditingSong(null); loadSongs(); }}
        />
      )}
    </AdminLayout>
  );
}
